import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import "../styles/leadDetail.css";

const API_URL = "https://crmapi.zauntrip.com/api/leads";

const BDM_NAMES = ["Sandeep", "Jaspal", "Aditya"];
const CURRENCIES = ["USD", "EUR", "GBP", "INR", "AED", "SGD", "AUD", "CAD", "THB", "JPY"];

const PRIORITY_STYLE = {
  Hot:  { background: "#fee2e2", color: "#b91c1c" },
  Warm: { background: "#fef3c7", color: "#92400e" },
  Cold: { background: "#dbeafe", color: "#1e40af" },
};

const stageColors = {
  New: "stage new", Contacted: "stage contacted", Qualified: "stage qualified",
  "Quotation Sent": "stage quotation", Negotiating: "stage negotiating",
  Won: "stage won", Lost: "stage lost",
};

function calcDuration(start, end) {
  if (!start || !end) return null;
  const ms = new Date(end) - new Date(start);
  const days = Math.round(ms / (1000 * 60 * 60 * 24));
  return days > 0 ? days : null;
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [edited, setEdited] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchLead = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/${id}`);
        if (!res.ok) throw new Error("Lead not found");
        const data = await res.json();
        setLead(data);
        setEdited(hydrateEdited(data));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchLead();
  }, [id]);

  // Convert DB lead → editable form shape
  const hydrateEdited = (data) => ({
    ...data,
    numberOfChildren: (data.children || []).length,
    childrenAges: (data.children || []).map((c) => String(c.age)),
    travelStartDate: data.travelStartDate ? data.travelStartDate.slice(0, 10) : "",
    travelEndDate: data.travelEndDate ? data.travelEndDate.slice(0, 10) : "",
  });

  const set = (field, value) => setEdited((e) => ({ ...e, [field]: value }));

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    set(name, type === "number" ? (parseInt(value) || 0) : value);
  };

  const handleNumberOfChildren = (e) => {
    const count = Math.max(0, parseInt(e.target.value) || 0);
    setEdited((e) => {
      const ages = [...(e.childrenAges || [])];
      while (ages.length < count) ages.push("");
      return { ...e, numberOfChildren: count, childrenAges: ages.slice(0, count) };
    });
  };

  const handleChildAge = (index, value) => {
    setEdited((e) => {
      const ages = [...e.childrenAges];
      ages[index] = value;
      return { ...e, childrenAges: ages };
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const children = (edited.childrenAges || []).map((a) => ({ age: parseInt(a) || 0 }));
      const payload = { ...edited, children };
      delete payload.childrenAges;
      delete payload.numberOfChildren;

      const res = await fetch(`${API_URL}/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to update lead");
      const updated = await res.json();
      setLead(updated);
      setEdited(hydrateEdited(updated));
      setIsEditing(false);
      alert("Lead updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to update lead. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      const res = await fetch(`${API_URL}/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete lead");
      alert("Lead deleted successfully!");
      navigate("/leads");
    } catch (err) {
      console.error(err);
      alert("Failed to delete lead.");
    }
  };

  const formatDate = (d) => {
    if (!d) return "N/A";
    return new Date(d).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });
  };

  if (loading) return (
    <div className="lead-detail-page">
      <div className="loading-container"><div className="loading-spinner" /><p>Loading lead details...</p></div>
    </div>
  );

  if (!lead) return (
    <div className="lead-detail-page">
      <div className="not-found-card">
        <h3>Lead not found</h3>
        <p>The requested lead could not be found or has been deleted.</p>
        <Link to="/leads" className="btn primary">Back to Leads</Link>
      </div>
    </div>
  );

  const duration = calcDuration(
    isEditing ? edited.travelStartDate : lead.travelStartDate,
    isEditing ? edited.travelEndDate : lead.travelEndDate
  );

  const priorityStyle = PRIORITY_STYLE[lead.priority] || {};

  return (
    <div className="lead-detail-page">
      <div className="lead-container">
        {/* Header actions */}
        <div className="header-actions">
          <Link to="/leads" className="back-link">← Back to Leads</Link>
          <div className="action-buttons">
            {!isEditing ? (
              <>
                <button className="btn primary" onClick={() => setIsEditing(true)}>Edit Lead</button>
                <button className="btn danger" onClick={handleDelete}>Delete Lead</button>
              </>
            ) : (
              <div className="action-group">
                <button className="btn primary" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
                <button className="btn outline" onClick={() => { setIsEditing(false); setEdited(hydrateEdited(lead)); }}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Lead header */}
        <div className="lead-header">
          <div>
            <h1>{lead.firstName} {lead.lastName}</h1>
            <div className="lead-meta">
              <span className="lead-id">ID: {lead.leadId || lead._id}</span>
              <span className="lead-date">Created: {formatDate(lead.createdDate)}</span>
              {lead.updatedDate && lead.updatedDate !== lead.createdDate && (
                <span className="lead-date">Updated: {formatDate(lead.updatedDate)}</span>
              )}
              {lead.createdBy && <span className="lead-id">BDM: {lead.createdBy}</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {lead.priority && (
              <span style={{ ...priorityStyle, padding: "4px 14px", borderRadius: 16, fontWeight: 700, fontSize: 13 }}>
                {lead.priority}
              </span>
            )}
            <span className={`lead-stage ${stageColors[lead.status] || "stage new"}`}>
              {lead.status || "New"}
            </span>
          </div>
        </div>

        {/* ── Lead Info ── */}
        <div className="card">
          <h3>Lead Information</h3>
          {isEditing ? (
            <div className="form-grid">
              <div className="form-group">
                <label>Created By (BDM)</label>
                <select name="createdBy" value={edited.createdBy || ""} onChange={handleChange}>
                  {BDM_NAMES.map((n) => <option key={n}>{n}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Lead Source</label>
                <select name="source" value={edited.source || ""} onChange={handleChange}>
                  {["Website","Social Media","Email","Phone","Walk-in","Travel Expo","Referral","Other"].map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Priority</label>
                <select name="priority" value={edited.priority || "Warm"} onChange={handleChange}>
                  <option>Hot</option><option>Warm</option><option>Cold</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select name="status" value={edited.status || "New"} onChange={handleChange}>
                  {["New","Contacted","Qualified","Quotation Sent","Negotiating","Won","Lost"].map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div className="info-grid">
              <div className="info-item"><label>BDM</label><p>{lead.createdBy || "—"}</p></div>
              <div className="info-item"><label>Source</label><p className="source-tag">{lead.source || "—"}</p></div>
              <div className="info-item"><label>Priority</label>
                <p>
                  {lead.priority ? (
                    <span style={{ ...priorityStyle, padding: "2px 10px", borderRadius: 12, fontWeight: 700, fontSize: 12 }}>
                      {lead.priority}
                    </span>
                  ) : "—"}
                </p>
              </div>
              <div className="info-item"><label>Status</label><p>{lead.status || "New"}</p></div>
            </div>
          )}
        </div>

        {/* ── Customer Details ── */}
        <div className="card">
          <h3>Customer Details</h3>
          {isEditing ? (
            <div className="form-grid">
              <div className="form-group">
                <label>First Name *</label>
                <input name="firstName" value={edited.firstName || ""} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input name="lastName" value={edited.lastName || ""} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" value={edited.email || ""} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Contact Number</label>
                <input name="phone" value={edited.phone || ""} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Nationality</label>
                <input name="nationality" value={edited.nationality || ""} onChange={handleChange} />
              </div>
            </div>
          ) : (
            <div className="info-grid">
              <div className="info-item"><label>Email</label><p>{lead.email || "—"}</p></div>
              <div className="info-item"><label>Phone</label><p>{lead.phone || "—"}</p></div>
              <div className="info-item"><label>Nationality</label><p>{lead.nationality || "—"}</p></div>
            </div>
          )}
        </div>

        {/* ── Package Requirement ── */}
        <div className="card">
          <h3>Package Requirement</h3>
          {isEditing ? (
            <div className="form-grid">
              <div className="form-group">
                <label>Type of Travel</label>
                <select name="travelType" value={edited.travelType || ""} onChange={handleChange}>
                  {["Leisure","Honeymoon","Group","Corporate","MICE","Adventure","Pilgrimage","Cultural","Other"].map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Destination Name</label>
                <input name="destinationName" value={edited.destinationName || ""} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Travel Start Date</label>
                <input type="date" name="travelStartDate" value={edited.travelStartDate || ""} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Travel End Date</label>
                <input type="date" name="travelEndDate" value={edited.travelEndDate || ""} onChange={handleChange} min={edited.travelStartDate} />
              </div>
              {duration !== null && (
                <div className="form-group full-width">
                  <p style={{ color: "#059669", fontWeight: 600 }}>
                    ✈️ Duration: {duration} day{duration !== 1 ? "s" : ""} / {duration - 1} night{duration - 1 !== 1 ? "s" : ""}
                  </p>
                </div>
              )}
              <div className="form-group">
                <label>No. of Adults</label>
                <input type="number" name="numberOfAdults" value={edited.numberOfAdults || 1} min="1" onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>No. of Children</label>
                <input type="number" value={edited.numberOfChildren || 0} min="0" onChange={handleNumberOfChildren} />
              </div>
              {(edited.numberOfChildren || 0) > 0 && (
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label>Age of Each Child</label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
                    {Array.from({ length: edited.numberOfChildren }).map((_, i) => (
                      <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                        <span style={{ fontSize: 11, color: "#6b7280" }}>Child {i + 1}</span>
                        <input
                          type="number" min="0" max="17"
                          value={edited.childrenAges?.[i] || ""}
                          onChange={(e) => handleChildAge(i, e.target.value)}
                          style={{ width: 68, textAlign: "center" }}
                          placeholder="Age"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="form-group">
                <label>No. of Infants</label>
                <input type="number" name="numberOfInfants" value={edited.numberOfInfants || 0} min="0" onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Preferred Currency</label>
                <select name="preferredCurrency" value={edited.preferredCurrency || "USD"} onChange={handleChange}>
                  {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label>Remarks</label>
                <textarea name="remarks" rows="3" value={edited.remarks || ""} onChange={handleChange} />
              </div>
            </div>
          ) : (
            <div className="info-grid">
              <div className="info-item"><label>Travel Type</label><p>{lead.travelType || "—"}</p></div>
              <div className="info-item"><label>Destination</label><p>{lead.destinationName || "—"}</p></div>
              <div className="info-item"><label>Start Date</label><p>{formatDate(lead.travelStartDate)}</p></div>
              <div className="info-item"><label>End Date</label><p>{formatDate(lead.travelEndDate)}</p></div>
              {duration !== null && (
                <div className="info-item">
                  <label>Duration</label>
                  <p style={{ color: "#059669", fontWeight: 600 }}>
                    {duration} days / {duration - 1} nights
                  </p>
                </div>
              )}
              <div className="info-item"><label>Adults</label><p>{lead.numberOfAdults ?? "—"}</p></div>
              <div className="info-item">
                <label>Children</label>
                <p>
                  {lead.children && lead.children.length > 0
                    ? `${lead.children.length} (Ages: ${lead.children.map((c) => c.age).join(", ")})`
                    : "0"}
                </p>
              </div>
              <div className="info-item"><label>Infants</label><p>{lead.numberOfInfants ?? 0}</p></div>
              <div className="info-item"><label>Currency</label><p>{lead.preferredCurrency || "—"}</p></div>
              {lead.remarks && (
                <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                  <label>Remarks</label><p>{lead.remarks}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}